import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import React, { useState, useEffect, useCallback,useMemo } from 'react';
import { useConfirmToast } from "../alertFunction.tsx";
import { addErrorLog }  from '../FirestoreDataLoader.tsx';
import * as pdfjsLib from 'pdfjs-dist'; 
import { PDFDocument } from 'pdf-lib'; 

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js`; 

type PageRanges = number[][]; 

interface PdfPagePreviewProps {
    pdfFile: File;
    pageNumber: number;
    width: number; // Larghezza rendering 
    errorAlert:(message: string, title:string) => void;
}

const PdfPagePreview: React.FC<PdfPagePreviewProps> = ({ pdfFile, pageNumber, width, errorAlert }) => {
    const canvasRef = React.useRef<HTMLCanvasElement>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Carica il documento e renderizza la pagina
    useEffect(() => {
        setIsLoading(true);

        const renderPage = async () => {
            if (!pdfFile || !canvasRef.current) return;

            try {
                // Legge il file in un ArrayBuffer
                const arrayBuffer = await pdfFile.arrayBuffer();
                
                // Carica il documento PDF
                const pdfDocument = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
                
                // Ottiene la pagina
                const page = await pdfDocument.getPage(pageNumber); 

                const viewport = page.getViewport({ scale: 1 });
                
                // Calcola la scala per adattare la larghezza desiderata (width)
                const scale = width / viewport.width;
                const scaledViewport = page.getViewport({ scale });

                const canvas = canvasRef.current;
                const context = canvas.getContext('2d');
                if (!context) return;
                
                // Imposta le dimensioni del canvas
                canvas.height = scaledViewport.height;
                canvas.width = scaledViewport.width;
                
                // Renderizza la pagina sul canvas
                await page.render({
                    canvasContext: context,
                    viewport: scaledViewport,
                }).promise;

                setIsLoading(false);
            } catch (err:any) {
                errorAlert(err,`Errore Pag. ${pageNumber}`);
                addErrorLog(`Errore Pag. ${pageNumber}`, err)
                setIsLoading(false);
            }
        };

        renderPage();
    }, [pdfFile, pageNumber, width]);

    return (
        <div 
            style={{
                width: `${width}px`,
                height: `${width * 1.25}px`, // Rapporto A4 approssimativo (width * 1.414) o un valore fisso (es. 100px)
                backgroundColor: '#fefefe',
                border: '1px solid #d1d5db',
                borderRadius: '3px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                position: 'relative',
            }}
        >
            {isLoading && (
                <div style={{color: '#9ca3af', fontSize: '0.75rem', padding: '5px', textAlign: 'center'}}>
                    Caricamento...
                </div>
            )}
           <canvas ref={canvasRef} style={{ display: (isLoading) ? 'none' : 'block' }} />
        </div>
    );
};


// Interfaccia per il divisore cliccabile
interface SplitDividerProps {
    pageNumber: number; // La pagina *dopo* cui avviene la divisione (es: 1 per dividere tra 1 e 2)
    isSplit: boolean;
    onClick: (pageNumber: number) => void;
}

// Componente: Divisore Cliccabile
const SplitDivider: React.FC<SplitDividerProps> = ({ pageNumber, isSplit, onClick }) => {

  const color = isSplit ? '#2563eb' : '#9ca3af'; 
  //const bgColor = isSplit ? '#eff6ff' : '#fefefe'; 
    
  const handleClick = () => {
    onClick(pageNumber);
  };

  return (
    <div
      onClick={handleClick} 
      style={{
        width: '30px', 
        height: '100%', 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer', 
        backgroundColor: 'transparent',
        flexShrink: 0, 
        position: 'relative', 
      }}
      title={`Clicca per ${isSplit ? 'rimuovere' : 'aggiungere'} la divisione dopo Pagina ${pageNumber}`}
    >
      {/* Linea tratteggiata */}
      <div 
        style={{
          height: '100%',
          width: '0px', 
          borderRight: `1px dashed ${color}`, 
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
        }}
      >
      </div>
      
      {/* Icona Forbici */}
      <ContentCutIcon 
        style={{ 
          fontSize: '1.5rem', 
          color,
          position: 'relative', 
          zIndex: 10,
          backgroundColor: '#fefefe', 
          padding: '0 3px', 
          borderRadius: '2px',
          transform: 'rotate(90deg) translateX(0)',
        }} 
      />
    </div>
  );
};


interface PdfSplitterModalProps {
    isOpen: boolean;
    onClose: () => void;
    onFilesReady: (files: File[]) => void;
}

//Componente per la divisione dei file PDF.
const PdfSplitterModal: React.FC<PdfSplitterModalProps> = ({ isOpen, onClose, onFilesReady }) => {
    
    const { errorAlert, warningAlert } = useConfirmToast(); 
    const [file, setFile] = useState<File | null>(null);
    const [numPages, setNumPages] = useState<number | null>(null);
    const [ranges, setRanges] = useState<PageRanges>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [splitPoints, setSplitPoints] = useState<Set<number>>(new Set());

    // hook ricalcola 'ranges' ogni volta che 'splitPoints' o 'numPages' cambiano.
    const calculateRangesFromSplitPoints = useCallback((points: Set<number>, totalPages: number | null): PageRanges => {
        if (!totalPages) return [];

        const newRanges: PageRanges = [];
        let currentPageStart = 1;

        for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
            // Se siamo all'ultima pagina O se la pagina corrente è un punto di divisione
            if (pageNum === totalPages || points.has(pageNum)) {
                // Crea il range per il file corrente
                const range: number[] = [];
                for (let p = currentPageStart; p <= pageNum; p++) {
                    range.push(p);
                }
                if (range.length > 0) {
                    newRanges.push(range);
                }
                // Imposta l'inizio del prossimo file alla pagina successiva
                currentPageStart = pageNum + 1;
            }
        }
        return newRanges;
    }, []);

    // Effect per mantenere 'ranges' sincronizzato con 'splitPoints'
    useEffect(() => {
        if (numPages) {
            const newRanges = calculateRangesFromSplitPoints(splitPoints, numPages);
            setRanges(newRanges);
        }
    }, [splitPoints, numPages, calculateRangesFromSplitPoints]);

    // GESTIONE CLICK SUL DIVISORE 
    const handleDividerClick = useCallback((pageNumber: number) => {
        setSplitPoints(prevPoints => {
            const newPoints = new Set(prevPoints);
            if (newPoints.has(pageNumber)) {
                newPoints.delete(pageNumber);
            } else {
                newPoints.add(pageNumber);
            }
            return newPoints;
        });
    }, []);
    
    // divisione rapida (1 pagina per file).
    const splitByPages = (): void => {
        if (!numPages) return;
        const allSplits = new Set<number>();
        for (let i = 1; i < numPages; i++) {
            allSplits.add(i);
        }
        setSplitPoints(allSplits);
    };

    
    // Funzioni 'loadPdfAndGetPageCount' e 'handleFileChange' rimangono invariate.
    const loadPdfAndGetPageCount = useCallback(async (pdfFile: File) => {
        if (!pdfFile) {
            errorAlert("La libreria PDFLib non è disponibile o il file non è valido.");
            addErrorLog("Errore","La libreria PDFLib non è disponibile o il file non è valido.");
            return;
        }
        
        setIsLoading(true);
        setNumPages(null);
        setRanges([]);
       // setCustomRangesInput("");
        setSplitPoints(new Set()); // Reset dei punti di divisione

        try {
            const arrayBuffer = await pdfFile.arrayBuffer();
            const pdfDoc = await PDFDocument.load(arrayBuffer);
            
            const pageCount: number = pdfDoc.getPageCount();
            setNumPages(pageCount);
            

        } catch (err) {
            errorAlert("Errore: Impossibile caricare il file PDF o contare le pagine.");
            addErrorLog("Impossibile caricare il file PDF o contare le pagine", err)
            setFile(null);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (file && isOpen) {
            loadPdfAndGetPageCount(file);
        }
    }, [file, isOpen, loadPdfAndGetPageCount]);

    const processSplits = async (): Promise<File[]> => {
        if (!file || ranges.length === 0) {
            const msg = "Carica un file valido e definisci i range prima di avviare la conversione.";
            errorAlert(msg);
            addErrorLog("Errore", msg)
            throw new Error(msg);
        }
            
        if (!PDFDocument) {
            const msg = "Errore: La libreria 'pdf-lib' (PDFDocument) non è disponibile.";
            errorAlert(msg);
            addErrorLog("Errore", msg)
            throw new Error(msg);
        }

        setIsLoading(true);

        try {
            const pdfBytes = await file.arrayBuffer();
            const originalPdf = await PDFDocument.load(pdfBytes);
            const outputFiles: File[] = [];

            for (let i = 0; i < ranges.length; i++) {
                const newPdf = await PDFDocument.create();
                
                // Pagine 1-based in ranges, 0-based in pdf-lib
                const pageIndicesToCopy: number[] = ranges[i].map(pageNum => pageNum - 1);
                
                if (pageIndicesToCopy.length > 0) {
                    // @ts-ignore
                    const copiedPages = await newPdf.copyPages(originalPdf, pageIndicesToCopy);
                    // @ts-ignore
                    copiedPages.forEach((copiedPage: any) => newPdf.addPage(copiedPage));
                }

                const pdfData: Uint8Array = await newPdf.save();
                const arrayBufferData = pdfData.buffer.slice(pdfData.byteOffset, pdfData.byteOffset + pdfData.byteLength) as ArrayBuffer;
                const blob = new Blob([arrayBufferData], { type: "application/pdf" });
                
                // Crea un oggetto File da restituire all'app madre
                const fileName = `${file.name.replace(/\.pdf$/i, "")}_P${i + 1}.pdf`;
                const newFile = new File([blob], fileName, { type: "application/pdf" });
                
                outputFiles.push(newFile);
            }
            return outputFiles;

        } catch (err:any) {
            errorAlert("Errore durante la conversione dei PDF:", err);
            addErrorLog("Errore durante la conversione dei PDF:", err)
            throw new Error("Errore durante la conversione dei PDF:", err);;
        } finally {
            setIsLoading(false);
        }
    };

    // Funzione per eseguire il download di un singolo File
    const triggerDownload = (file: File) => {
        const url: string = URL.createObjectURL(file);
        const link: HTMLAnchorElement = document.createElement("a");
        link.href = url;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }


    // Avvia Conversione (solo ritorno dati all'app)
    const handleConvertOnly = async () => {
        if (isLoading || ranges.length === 0) return;
        try {
            const files = await processSplits();
            onFilesReady(files); // Passa i file all'app madre
            onClose(); // Chiude la modale
        } catch (e) {
            // L'errore è gestito da processSplits
        }
    };
    
    // Esporta File (solo download)
    const handleExportOnly = async () => {
        if (isLoading || ranges.length === 0) return;
        try {
            const files = await processSplits();
            
            files.forEach(file => triggerDownload(file));
            onClose(); 
        } catch (e) {
            // L'errore è gestito da processSplits
        }
    };

    // Avvia Conversione ed Esporta (ritorno dati + download)
    const handleConvertAndExport = async () => {
        if (isLoading || ranges.length === 0) return;
        try {
            const files = await processSplits();
            
            files.forEach(file => triggerDownload(file));
            
            onFilesReady(files); // Passa i file all'app madre
            onClose(); 

        } catch (e) {
            // L'errore è gestito da processSplits
        }
    };
    

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files ? event.target.files[0] : null;

        if (selectedFile && selectedFile.type === "application/pdf") {
            setFile(selectedFile);
            setNumPages(null);
            setRanges([]);
            setSplitPoints(new Set()); 
        } else if (selectedFile) {
            warningAlert("Per favore, seleziona un file PDF valido.");
            setFile(null);
            setNumPages(null);
            setRanges([]);
        }
        event.target.value = '';
    };

    if (!isOpen) return null;

    // Array di pagine per la visualizzazione. Es: [1, 2, 3, 4, 5] se numPages = 5
    const pageNumbersArray: number[] = useMemo(() => 
        numPages ? Array.from({ length: numPages }, (_, i) => i + 1) : [], 
    [numPages]);

    const disabled = ranges.length === 0 || isLoading;

    return (
        <div className="modal-overlay">
            <div className="modal-content" style={{width:'auto', minWidth:'40%',maxWidth:'56%', padding:'0 2.5rem 2.5rem 2.5rem'}}>
                
                {/* Titolo e Dettagli File */}
                <button onClick={onClose} className="X2close" style={{userSelect:'none', height:'2rem', position:'absolute', fontSize:'1rem' ,right:0}}>✕</button>
                <div style={{height:'2rem'}}></div>
                <h2  
                    style={{ 
                        userSelect:'none',
                        fontSize: '1.5rem',  
                        fontFamily: "Segoe UI",
                        fontWeight: 800,  
                        color: '#1f2937',  
                        marginBottom: '1rem',  
                        paddingBottom: '0.5rem',  
                        borderBottom: '1px solid #e5e7eb',  
                    }} 
                >
                    Dividi un PDF in più files
                </h2>
                {!file && (
                    <div style ={{ display:'flex', alignContent: 'center', backgroundColor: '#0e2f70ff', paddingLeft: '1.2rem', borderRadius: '0.5rem', marginBottom:'1rem', padding:'0.75rem' }}>
                        <label  
                            htmlFor="pdf-input"
                            style={{  
                                userSelect:'none',
                                display: 'flex',  
                                alignItems: 'center',  
                                color: 'white',  
                                cursor: 'pointer',
                                fontWeight: 600,
                                marginLeft:'0.6rem',
                                width:'100%'
                            }}
                        >
                            <CloudUploadIcon style={{ fontSize: '1.25rem', marginRight: '0.5rem' }} />
                            Carica un PDF
                        </label>
                        <input  
                            id="pdf-input"  
                            type="file"  
                            accept="application/pdf"
                            onChange={handleFileChange}  
                            style={{ display: 'none' }}  
                        />
                    </div>
                )}    
                {file && (
                    <p  
                        style={{ 
                            fontSize: '0.875rem', 
                            color: '#4b5563',  
                            marginBottom: '1rem',  
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis', 
                            userSelect:'none', 
                        }}
                    >
                        File corrente: <span style={{ fontWeight: 600, color: '#0f40acff',}}>{file?.name || "Nessun file"}</span>
                    </p>
                )}

                {/* Info Pagine */}
                {numPages && (
                    <div  
                        style={{ 
                            userSelect:'none',
                            backgroundColor: '#f3f4f6',  
                            color: '#374151',  
                            fontWeight: 500,
                            padding: '0.75rem',
                            marginBottom: '1rem',
                            borderRadius: '0.5rem',
                        }}
                    >
                        Totale Pagine: <span style={{fontWeight: 700, color: '#0f40acff'}}>{numPages}</span>
                    </div>
                )}
                
                {/* Anteprima visuale */}
                {numPages && (
                    <div style={{
                        marginBottom: '1.5rem',
                        padding: '1rem 0',
                        borderTop: '1px solid #d1d5db',
                        borderBottom: '1px solid #d1d5db',
                        backgroundColor: '#fefefe',
                        maxHeight: '20rem',
                        overflowY: 'auto',
                    }}>
                        <h3 style={{userSelect:'none', fontWeight: 700, fontSize: '1.125rem', color: '#374151', marginBottom: '0.75rem', paddingLeft: '1rem'}}>
                            Anteprima Pagine:
                        </h3>
                        
                        <div style={{
                            display: 'flex',
                            flexDirection:'row',
                            alignItems: 'stretch', 
                            overflowX: 'auto',
                            padding: '0 1rem',
                            gap: '10px',
                        }}>
                            {pageNumbersArray.map((pageNum) => (
                                <React.Fragment key={pageNum}>
                                    {/* Anteprima foglio PDF */}
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        flexShrink: 0,
                                        width: '130px', 
                                      }}
                                    >
                                      {file ? (
                                        <PdfPagePreview
                                          pdfFile={file}
                                          pageNumber={pageNum}
                                          width={130} 
                                          errorAlert={errorAlert}
                                          />
                                        ) : (
                                          // Fallback se il file non è disponibile
                                          <div style={{ width: '100%', height: '100px', backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #d1d5db', borderRadius: '3px' }}>
                                            P. {pageNum}
                                          </div>
                                        )}
                                        {/* Numero di pagina sotto l'anteprima */}
                                        <p style={{
                                            userSelect:'none',
                                            fontSize: '0.75rem',
                                            color: '#6b7280',
                                            marginTop: '0.25rem',
                                        }}>
                                           {pageNum}
                                        </p>
                                    </div>
                                    {/* DIVISORE CLICCABILE */}
                                    {pageNum < numPages! && (
                                      <div style={{height:'160px'}}>
                                        <SplitDivider
                                            pageNumber={pageNum}
                                            isSplit={splitPoints.has(pageNum)}
                                            onClick={handleDividerClick}
                                        />
                                      </div>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                )}
                
                {/* Anteprima Divisione */}
                 <div  
                    style={{ 
                        backgroundColor: '#f9fafb', 
                        padding: '1rem', 
                        borderRadius: '0.5rem', 
                        boxShadow: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)', 
                        marginBottom: '1.5rem', 
                    }} 
                >
                    <h3 style={{userSelect:'none', fontWeight: 700, fontSize: '1.125rem', fontFamily: "Segoe UI", color: '#374151',marginBottom: '0.5rem'}}>Divisione Attuale ({ranges.length} File):</h3> 
                    <div  
                        style={{ 
                            minHeight: '50px', 
                            maxHeight: '8rem',  
                            overflowY: 'auto', 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: '0.28rem',  
                        }} 
                    > 
                        {ranges.length > 0 ? ranges.map((r, idx) => ( 
                            <div  
                                key={idx}  
                                style={{ 
                                    padding: '0.25rem 0', 
                                    borderBottom: idx < ranges.length - 1 ? '1px dotted #e5e7eb' : 'none', 
                                }} 
                            > 
                                <span style={{fontWeight: 500, color: '#0f40acff'}}>{file?.name.replace(/\.pdf$/i, "")}_P{idx + 1}.pdf</span>: pagina {r.join(" - ")} 
                            </div> 
                        )) : ( 
                            <p style={{fontSize: '0.875rem', color: '#6b7280', fontStyle: 'italic'}}>{file ? '' : 'Carica un file PDF per iniziare.'}</p> 
                        )} 
                    </div> 
                </div>

                {/* Controlli */}
                <div  
                    style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '0.75rem', 
                        paddingTop: '1rem',  
                        borderTop: '1px solid #e5e7eb',  
                    }} 
                >
                    <button  
                        onClick={splitByPages}
                        style={{
                            userSelect:'none',  
                            width: '100%', 
                            paddingTop: '0.75rem',  
                            paddingBottom: '0.75rem',  
                            fontWeight: 600,  
                            borderRadius: '0.5rem',   
                            border:0, 
                            transition: 'background-color 0.15s ease-in-out',  
                            backgroundColor: !numPages || isLoading? '#0f41ac6e':'#0e2f70ff',  
                            color: 'white', 
                            cursor: !numPages || isLoading? 'not-allowed' : 'pointer', 
                        }} 
                        disabled={!numPages || isLoading} 
                        title={"1 Pagina per File"}
                    > 
                        Divisione Rapida
                    </button>
                     {/* Gruppo di Azioni Secondarie */}
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        {/* Avvia Conversione */}
                        <button 
                            onClick={handleConvertOnly} 
                            disabled={disabled}
                            style={{ 
                                userSelect:'none',
                                width: '100%', 
                                paddingTop: '0.75rem',  
                                paddingBottom: '0.75rem',  
                                fontWeight: 600,  
                                borderRadius: '0.5rem',  
                                border:0, 
                                transition: 'background-color 0.15s ease-in-out, opacity 0.15s ease-in-out',
                                flex: 1,
                                backgroundColor: disabled? '#12788a7a':'#2683a0ff',
                                color: '#fff',
                                cursor: disabled ? 'not-allowed' : 'pointer',
                            }}
                        >
                            Avvia Conversione
                        </button>
                        
                        {/* Esporta File */}
                        <button 
                            onClick={handleExportOnly} 
                            disabled={disabled}
                            style={{ 
                                userSelect:'none',
                                width: '100%', 
                                paddingTop: '0.75rem',  
                                paddingBottom: '0.75rem',  
                                fontWeight: 600,  
                                borderRadius: '0.5rem',  
                                border:0, 
                                transition: 'background-color 0.15s ease-in-out, opacity 0.15s ease-in-out',
                                flex: 1,
                                backgroundColor: disabled? '#12788a7a':'#2683a0ff',
                                color: '#fff',
                                cursor: disabled ? 'not-allowed' : 'pointer',
                            }}
                        >
                            Esporta Files
                        </button>
                    </div>
                    
                    {/* Avvia Conversione ed Esporta */}
                    <button 
                        onClick={handleConvertAndExport} 
                        disabled={disabled}
                        style={{ 
                            userSelect:'none',
                            width: '100%', 
                            paddingTop: '0.75rem',  
                            paddingBottom: '0.75rem',  
                            fontWeight: 600,  
                            borderRadius: '0.5rem',  
                            border:0, 
                            transition: 'background-color 0.15s ease-in-out, opacity 0.15s ease-in-out',
                            backgroundColor: disabled? '#128a667a':'#11af73ff',  
                            color: '#fff', 
                            cursor: disabled ? 'not-allowed' : 'pointer',
                        }} 
                    > 
                        {isLoading ? "Conversione in corso..." : `Avvia Conversione ed Esporta`} 
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PdfSplitterModal